import React, { useState, useEffect } from 'react';
import { Home, Settings, Star, HelpCircle, Activity, TrendingUp, Search, LogOut, User, Menu, X, GraduationCap, Zap, Users, ShoppingBag, Briefcase, MessageSquare, Ticket, Key, Lightbulb } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { useSiteStats } from '../SiteStatsContext';
import { useAuth } from '../AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant d'icône Discord optimisé
const DiscordIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 127.14 96.36" fill="currentColor">
    <g>
      <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
    </g>
  </svg>
);

const iconMap = {
  home: Home,
  settings: Settings,
  star: Star,
  'help-circle': HelpCircle,
  activity: Activity,
  'trending-up': TrendingUp,
  'graduation-cap': GraduationCap,
  'zap': Zap,
  'users': Users,
  'shopping-bag': ShoppingBag,
  'briefcase': Briefcase,
  'message-square': MessageSquare,
  'ticket': Ticket,
  'key': Key,
  'lightbulb': Lightbulb
};

const Sidebar = ({ activeSection, setActiveSection, mobileMenuOpen, setMobileMenuOpen }) => {  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const stats = useSiteStats();
  const { isAuthenticated, user } = useAuth();
  const [hasOrder, setHasOrder] = useState(false);
  const [hasLicence, setHasLicence] = useState(false);

  // User is "client" if they have the Discord Client role (backend-authoritative flag)
  const isClient = (user && user.is_client === true);

  const currentPath = location.pathname;

  // Check if user has orders or is admin
  useEffect(() => {
    const checkOrders = async () => {
      if (!isAuthenticated) {
        setHasOrder(false);
        return;
      }

      // Les admins ont toujours accès
      if (user?.is_admin) {
        setHasOrder(true);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const orders = await response.json();
          setHasOrder(orders.length > 0);
        }
      } catch (error) {
        console.error('Error checking orders:', error);
      }
    };

    checkOrders();
  }, [isAuthenticated, user]);

  // Check if user has a licence (re-check on each navigation to detect deletions)
  useEffect(() => {
    if (!isAuthenticated) { setHasLicence(false); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/user/licence-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setHasLicence(d.has_licence === true))
      .catch(() => {});
  }, [isAuthenticated, currentPath]);

  const navigationItems = [
    { id: 1, name: t('nav.home'), path: "/", icon: "home" },
    { id: 2, name: t('nav.services'), path: "/services", icon: "settings" },
    ...(isClient ? [{ id: 7, name: "Licences", path: "/licences", icon: "key" }] : []),
    { id: 4, name: t('nav.reviews'), path: "/feedbacks", icon: "star" },
    { id: 5, name: t('nav.faq'), path: "/faq", icon: "help-circle" },
    { id: 6, name: "Recrutement", path: "/recrutement", icon: "briefcase" },
    { id: 8, name: "Suggestions", path: "/suggestions", icon: "lightbulb" }
  ];

  const performanceLinks = [
    { id: 2, name: t('nav.benchmarkImprovement'), path: "/benchmark", icon: "trending-up" }
  ];

  // Account items - shown when user is authenticated
  const accountItems = [
    { id: 1, name: t('sidebar.myOrders'), path: "/mes-commandes", icon: "shopping-bag" },
    { id: 2, name: t('sidebar.parrainage'), path: "/parrainage", icon: "users" }
  ];

  // Client space items - "Tutoriels Windows" only shown to clients (licence OR Client role)
  const clientSpaceItems = [
    ...((hasLicence || isClient) ? [{ id: 1, name: t('sidebar.windowsTutorials'), path: "/espace-client", icon: "graduation-cap" }] : []),
    { id: 2, name: "AqmOptiTool", path: "/aqmoptitool", icon: "zap" },
    ...(hasLicence ? [{ id: 4, name: "Ma Licence", path: "/ma-licence", icon: "key" }] : []),
    { id: 3, name: "Support", path: "/support", icon: "ticket" }
  ];

  const handleNavigation = (path) => {
    setActiveSection(path);
    navigate(path);
    if (setMobileMenuOpen) setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar 3D Style */}
      <aside className={`
        fixed left-0 top-0 h-full w-64 lg:w-52 bg-[#0a0b14] border-r border-cyan-900/30 flex flex-col z-50
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>

        {/* Mobile Close Button */}
        <button 
          className="lg:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-white"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Logo */}
        <div className="p-4 border-b border-cyan-900/30">
          <img 
            src="/aqmopti-logo.png" 
            alt="AqmOpti Logo" 
            className="w-16 lg:w-20 h-16 lg:h-20 mx-auto mb-1 hover:scale-110 transition-transform duration-300 object-contain rounded-xl"
            style={{ imageRendering: 'crisp-edges', mixBlendMode: 'lighten' }}
          />
          <p className="text-center text-cyan-500 font-bold text-sm">AqmOpti</p>
          
          {/* Mobile only - Clients satisfaits banner */}
          <div className="lg:hidden mt-3 py-2.5 px-3 rounded-lg border border-cyan-600/40 bg-cyan-950/40 flex items-center justify-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            <span className="text-cyan-400 font-semibold text-sm">+{stats.optimizedPCs}</span>
            <span className="text-gray-300 text-sm">{t('sidebar.satisfiedClients')}</span>
          </div>
        </div>

        {/* Search */}
        <div className="hidden lg:block px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input 
              type="text" 
              placeholder={t('nav.search')} 
              className="pl-10 bg-zinc-900 border-cyan-900/30 text-white placeholder:text-gray-500 focus:border-cyan-600 transition-all duration-200"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-4 custom-scrollbar">
          <div className="mb-6">
            <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('nav.navigation')}</h3>
            {navigationItems.map((item) => {
              const Icon = iconMap[item.icon] || HelpCircle;
              const isActive = currentPath === item.path;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1.5 transition-all duration-200 group relative ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-700/90 to-cyan-600/90 text-white shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                      : 'text-gray-400 hover:bg-zinc-800/60 hover:text-white hover:pl-4'
                  }`}
                >
                  <Icon className={`w-4 h-4 transition-transform duration-200 ${isActive ? 'drop-shadow-sm' : 'group-hover:scale-110'}`} />
                  <span className="text-sm font-medium">{item.name}</span>
                  {isActive && (
                    <div className="absolute right-2 w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mb-6">
            <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('nav.performance')}</h3>
            {performanceLinks.map((item) => {
              const Icon = iconMap[item.icon] || TrendingUp;
              const isActive = currentPath === item.path;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1.5 transition-all duration-200 group relative ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-700/90 to-cyan-600/90 text-white shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                      : 'text-gray-400 hover:bg-zinc-800/60 hover:text-white hover:pl-4'
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isActive ? 'drop-shadow-sm' : 'group-hover:scale-110'}`} />
                  <span className="text-xs font-medium whitespace-nowrap">{item.name}</span>
                  {isActive && (
                    <div className="absolute right-2 w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Compte - shown when user is authenticated */}
          {isAuthenticated && (
            <div className="mb-6">
              <h3 className="px-3 mb-2 text-xs font-semibold text-purple-400/80 uppercase tracking-wider">{t('sidebar.account')}</h3>
              {accountItems.map((item) => {
                const Icon = iconMap[item.icon] || ShoppingBag;
                const isActive = currentPath === item.path;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1.5 transition-all duration-200 group relative ${
                      isActive
                        ? 'bg-gradient-to-r from-purple-700/80 to-purple-600/80 text-white shadow-[0_0_12px_rgba(147,51,234,0.15)]'
                        : 'text-purple-400/80 hover:bg-purple-900/20 hover:text-white hover:pl-4 border border-purple-800/20'
                    }`}
                  >
                    <Icon className={`w-4 h-4 transition-transform duration-200 ${isActive ? 'drop-shadow-sm' : 'group-hover:scale-110'}`} />
                    <span className="text-sm font-medium">{item.name}</span>
                    {isActive && (
                      <div className="absolute right-2 w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Espace Client — visible only for users with active licence OR Discord Client role */}
          {(hasLicence || isClient) && (
            <div className="mb-6">
              <h3 className="px-3 mb-2 text-xs font-semibold text-emerald-400/80 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500/70 rounded-full animate-pulse"></span>
                {t('sidebar.clientSpace')}
              </h3>
              {clientSpaceItems.map((item) => {
                const Icon = iconMap[item.icon] || Zap;
                const isActive = currentPath === item.path;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1.5 transition-all duration-200 group relative ${
                      isActive
                        ? 'bg-gradient-to-r from-emerald-700/80 to-emerald-600/80 text-white shadow-[0_0_12px_rgba(34,197,94,0.15)]'
                        : 'text-emerald-400/80 hover:bg-emerald-900/20 hover:text-white hover:pl-4 border border-emerald-800/20'
                    }`}
                  >
                    <Icon className={`w-4 h-4 transition-transform duration-200 ${isActive ? 'drop-shadow-sm' : 'group-hover:scale-110'}`} />
                    <span className="text-sm font-medium">{item.name}</span>
                    {isActive && (
                      <div className="absolute right-2 w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Réseaux sociaux */}
          <div>
            <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('nav.ourNetworks')}</h3>
            <div className="px-3 flex items-center gap-3">
              <a 
                href="https://twitter.com/aqmopti"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 border border-cyan-900/30 hover:border-cyan-600/50 hover:bg-cyan-950/30 hover:scale-110 transition-all duration-200 group"
                title="X (Twitter)"
              >
                <svg className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@aqmoptimisation"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 border border-cyan-900/30 hover:border-cyan-600/50 hover:bg-cyan-950/30 hover:scale-110 transition-all duration-200 group"
                title="TikTok"
              >
                <svg className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://discord.gg/aqmopti"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 border border-cyan-900/30 hover:border-cyan-600/50 hover:bg-cyan-950/30 hover:scale-110 transition-all duration-200 group"
                title="Discord"
              >
                <DiscordIcon className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>
        </nav>

        {/* Discord Login Button */}
        <div className="p-4 border-t border-cyan-900/30">
          <SidebarLoginButton />
        </div>
      </aside>
    </>
  );
};

// Separate component for login button to use auth hook
const SidebarLoginButton = () => {
  const { t } = useLanguage();
  const { user, isAuthenticated, loginWithDiscord, logout } = useAuth();

  const getAvatarUrl = () => {
    if (user?.avatar) {
      return `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`;
    }
    return null;
  };

  if (isAuthenticated) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-cyan-900/30 rounded-lg">
          {getAvatarUrl() ? (
            <img src={getAvatarUrl()} alt={user.username} className="w-8 h-8 rounded-full" />
          ) : (
            <User className="w-5 h-5 text-gray-400" />
          )}
          <span className="text-sm text-white font-medium truncate flex-1">{user.username}</span>
        </div>
        <Button 
          onClick={logout}
          variant="outline"
          className="w-full text-red-400 border-red-900/30 hover:text-red-300 hover:border-red-500/50 hover:bg-red-950/20"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t('sidebar.logout')}
        </Button>
      </div>
    );
  }

  return (
    <Button 
      onClick={loginWithDiscord}
      className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-xl rounded-xl flex items-center justify-center gap-2"
      data-testid="sidebar-login-btn"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
      </svg>
      Login Discord
    </Button>
  );
};

export default Sidebar;
